import { useRef, useState, type ComponentProps, type FormEvent, type JSX } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Form, FormActions, FormField, FormMessage } from '@/components/form';
import { useAuth } from '@/providers/AuthProvider';
import { PillButton } from '@/components/entity';
import type { SignInErrorReason } from '@/lib/services/auth';
import { trackAuthSignInFailure } from '@/lib/analytics/events';

type AuthMode = 'login' | 'forgot';

interface FormState {
  email: string;
  password: string;
}

interface AuthDialogProps {
  triggerLabel?: string;
  triggerVariant?: ComponentProps<typeof PillButton>['variant'];
  triggerSize?: ComponentProps<typeof PillButton>['size'];
  triggerClassName?: string;
}

const SIGN_IN_ERROR_MESSAGES: Record<SignInErrorReason, string> = {
  invalid_credentials: 'Email or password is incorrect.',
  user_not_found: 'Email or password is incorrect.',
  rate_limited: 'Too many attempts. Please wait a moment before trying again.',
  unknown: 'Something went wrong. Please try again.',
};

const initialFormState: FormState = {
  email: '',
  password: '',
};

export function AuthDialog({
  triggerLabel = 'Sign in',
  triggerVariant = 'secondary',
  triggerSize = 'md',
  triggerClassName,
}: AuthDialogProps): JSX.Element {
  const { signIn, requestPasswordReset } = useAuth();
  const [openExplicit, setOpenExplicit] = useState(false);
  const [mode, setMode] = useState<AuthMode>('login');
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<SignInErrorReason | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSubmitted, setResetSubmitted] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const errorMessageRef = useRef<HTMLDivElement | null>(null);

  const focusErrorMessage = () => {
    window.setTimeout(() => {
      errorMessageRef.current?.focus();
    }, 0);
  };

  const setFormErrorState = (message: string | null, reason: SignInErrorReason | null = null) => {
    setFormError(message);
    setErrorReason(reason);
  };

  const showFormError = (message: string, reason: SignInErrorReason | null = null) => {
    setFormErrorState(message, reason);
    focusErrorMessage();
  };

  const resetDialogState = () => {
    setMode('login');
    setFormState(initialFormState);
    setFormErrorState(null, null);
    setIsSubmitting(false);
    setResetSubmitted(false);
  };

  const closeDialog = () => {
    setOpenExplicit(false);
    resetDialogState();
  };

  const markInvalidControls = (form: HTMLFormElement) => {
    const elements = Array.from(form.elements).filter(
      (element): element is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement =>
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement,
    );

    elements.forEach((element) => {
      if (element.validity.valid) {
        element.removeAttribute('aria-invalid');
      } else {
        element.setAttribute('aria-invalid', 'true');
      }
    });
  };

  const handleLoginSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.preventDefault();
      markInvalidControls(form);
      form.reportValidity();
      return;
    }

    event.preventDefault();
    setFormError(null);
    setErrorReason(null);
    markInvalidControls(form);

    const formData = new FormData(form);
    const email = (formData.get('email') as string | null)?.trim() ?? '';
    const passwordInput = formData.get('password');
    const password =
      typeof passwordInput === 'string' && passwordInput.length > 0 ? passwordInput : '';

    if (email.length === 0 || password.length === 0) {
      showFormError('Enter both your email address and password.');
      return;
    }

    setIsSubmitting(true);
    const result = await signIn({ email, password });
    setIsSubmitting(false);

    if (result.status === 'success') {
      closeDialog();
      return;
    }

    const message = SIGN_IN_ERROR_MESSAGES[result.reason];
    showFormError(message, result.reason);
    if (import.meta.env.DEV) {
      console.info('AuthDialog sign-in failed', result);
    }
    trackAuthSignInFailure({ reason: result.reason, origin: 'auth_dialog' });

    const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
    const passwordField = form.querySelector<HTMLInputElement>('input[name="password"]');

    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'true');
    }
    if (passwordField) {
      passwordField.setAttribute('aria-invalid', 'true');
    }

    setFormState((previous) => ({
      ...previous,
      password: '',
    }));
  };

  const handleResetSubmit = async (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.preventDefault();
      markInvalidControls(form);
      form.reportValidity();
      return;
    }

    event.preventDefault();
    setFormError(null);
    markInvalidControls(form);

    const formData = new FormData(form);
    const email = (formData.get('email') as string | null)?.trim() ?? '';

    if (!email.length) {
      showFormError('Enter the email address associated with your account.');
      return;
    }

    setIsSubmitting(true);
    try {
      await requestPasswordReset(email);
      setResetSubmitted(true);
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'We could not start the reset process. Please try again.';
      showFormError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = (nextMode: AuthMode) => {
    setMode(nextMode);
    setFormErrorState(null, null);
    setIsSubmitting(false);
    setResetSubmitted(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setOpenExplicit(true);
      return;
    }
    closeDialog();
  };

  const isDialogOpen = openExplicit || formError !== null;

  return (
    <Dialog.Root open={isDialogOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <PillButton variant={triggerVariant} size={triggerSize} className={triggerClassName}>
          {triggerLabel}
        </PillButton>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-surface-navy/40 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-brand-primary/20 bg-white p-6 shadow-card focus:outline-none"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            window.setTimeout(() => {
              emailInputRef.current?.focus();
            }, 0);
          }}
          onEscapeKeyDown={() => {
            closeDialog();
          }}
        >
          <Dialog.Title className="text-2xl font-semibold text-brand-primary">
            {mode === 'login' ? 'Welcome back' : 'Reset your password'}
          </Dialog.Title>
          <Dialog.Description className="mt-xs text-sm text-text-primary/70">
            {mode === 'login'
              ? 'Sign in to manage your Strashin Developments portfolio. New accounts are invite-only—contact an administrator if you need access.'
              : resetSubmitted
                ? 'We sent reset instructions to your email. Follow the link to create a new password.'
                : 'Provide the email associated with your account and we’ll email you a reset link.'}
          </Dialog.Description>

          <div className="mt-md">
            {mode === 'login' ? (
              <Form gap="md" onSubmit={handleLoginSubmit}>
                <FormField id="auth-email" label="Email address" required>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="w-full rounded-lg border border-brand-primary/25 bg-white px-3 py-2.5 text-base text-text-primary transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:border-brand-primary/15 md:py-2 md:text-sm"
                    ref={emailInputRef}
                    value={formState.email}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      event.currentTarget.removeAttribute('aria-invalid');
                      setFormState((previous) => ({ ...previous, email: nextValue }));
                    }}
                  />
                </FormField>

                <FormField
                  id="auth-password"
                  label="Password"
                  required
                  labelSecondary={
                    <button
                      type="button"
                      className="text-xs font-medium text-brand-accent hover:underline"
                      onClick={() => {
                        switchMode('forgot');
                      }}
                    >
                      Forgot password?
                    </button>
                  }
                >
                  <input
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    className="w-full rounded-lg border border-brand-primary/25 bg-white px-3 py-2.5 text-base text-text-primary transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:border-brand-primary/15 md:py-2 md:text-sm"
                    value={formState.password}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      event.currentTarget.removeAttribute('aria-invalid');
                      setFormState((previous) => ({
                        ...previous,
                        password: nextValue,
                      }));
                    }}
                  />
                </FormField>

                {formError !== null ? (
                  <FormMessage
                    ref={errorMessageRef}
                    tone="error"
                    role="alert"
                    ariaLive="assertive"
                    tabIndex={-1}
                    data-error-reason={errorReason ?? undefined}
                    data-testid="auth-error-message"
                  >
                    {formError}
                  </FormMessage>
                ) : null}

                <FormActions>
                  <PillButton type="submit" className="h-11 px-6" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      'Sign in'
                    )}
                  </PillButton>
                </FormActions>
              </Form>
            ) : (
              <Form gap="md" onSubmit={handleResetSubmit}>
                <FormField
                  id="reset-email"
                  label="Email address"
                  required
                  hint="We’ll send password reset instructions to this address."
                >
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    className="w-full rounded-lg border border-brand-primary/25 bg-white px-3 py-2.5 text-base text-text-primary transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:border-brand-primary/15 md:py-2 md:text-sm"
                    ref={emailInputRef}
                    value={formState.email}
                    disabled={resetSubmitted}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      event.currentTarget.removeAttribute('aria-invalid');
                      setFormState((previous) => ({ ...previous, email: nextValue }));
                    }}
                  />
                </FormField>

                {formError !== null ? (
                  <FormMessage
                    ref={errorMessageRef}
                    tone="error"
                    role="alert"
                    ariaLive="assertive"
                    tabIndex={-1}
                    data-error-reason={errorReason ?? undefined}
                    data-testid="auth-error-message"
                  >
                    {formError}
                  </FormMessage>
                ) : null}
                {resetSubmitted ? (
                  <FormMessage tone="success">
                    Check your inbox for a message from Strashin Developments to finish resetting
                    your password.
                  </FormMessage>
                ) : null}

                <FormActions justify="between">
                  <button
                    type="button"
                    className="text-sm font-medium text-brand-accent hover:underline"
                    onClick={() => {
                      switchMode('login');
                    }}
                  >
                    Back to sign in
                  </button>
                  <PillButton
                    type="submit"
                    className="h-11 px-6"
                    disabled={isSubmitting || resetSubmitted}
                  >
                    {isSubmitting ? (
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : resetSubmitted ? (
                      'Email sent'
                    ) : (
                      'Send reset link'
                    )}
                  </PillButton>
                </FormActions>
              </Form>
            )}
          </div>

          <Dialog.Close asChild>
            <button
              type="button"
              className="absolute right-4 top-4 rounded-full p-2 text-text-primary/60 transition hover:text-brand-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
              aria-label="Close authentication dialog"
              onClick={closeDialog}
            >
              <span className="sr-only">Close</span>✕
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
